/**
 * WHAT AN ANSWER SAYS ALOUD (VOICE1, size A - the 130th, 2026-10-11).
 *
 * His words: "can we add a voice to the AI chat ... if it could talk back to me and be like, yeah, give me the name
 * and the address of the owner ... kind of like a person." The chat's answers are written as markdown for the EYE -
 * headings, bullets, tables, links. This turns one into what is SAID: plain sentences, one entry each.
 *
 * ⛔ A FIGURE IN A SENTENCE IS NEVER DROPPED, ROUNDED OR SPLIT. The house rule is "defer prose, never a number": a TABLE
 *   is deferred to the screen in one sentence ("A table of 5 rows is on the screen.") and so is a block of code or a web
 *   address - but "$261,625.50", "5.75%" and "1.25x" inside a sentence are said exactly as written.
 * ⭐ ONE ENTRY PER SENTENCE, so the reader can stop between two of them and never in the middle of a figure.
 * ⭐ PURE. No speech API here - `useReadAloud` owns the device; this owns the words, and is tested without one.
 */

/** A dot after one of these does not end the sentence: "13368 Main St. is listed", "MDTH Inc. is the seller". */
const ABBREVIATIONS = new Set([
  'St', 'Ave', 'Blvd', 'Rd', 'Dr', 'Ln', 'Ct', 'Pl', 'Hwy', 'Pkwy', 'Ste', 'Apt', 'Bldg', 'Rte',
  'N', 'S', 'E', 'W', 'No', 'Inc', 'Co', 'Corp', 'Ltd', 'Mr', 'Mrs', 'Ms', 'Jr', 'Sr',
  'approx', 'Approx', 'est', 'Est', 'vs', 'sq', 'ft', 'e.g', 'i.e', 'U.S', 'etc',
]);

const TABLE_LINE = /^\s*\|.*\|\s*$/;
const TABLE_SEPARATOR = /^\s*\|?\s*:?-+:?\s*(\|\s*:?-+:?\s*)*\|?\s*$/;
const RULE_LINE = /^\s*([-*_])(\s*\1){2,}\s*$/;
const FENCE = /^\s*(```|~~~)/;

const TABLE_SAID = (rows: number): string => `A table of ${rows} ${rows === 1 ? 'row' : 'rows'} is on the screen.`;
const CODE_SAID = 'A block of code is on the screen.';
const LINK_SAID = 'a link on the screen';

/** The marks that are only for the eye, removed from one run of text. The words - and every figure - stay. */
const plain = (text: string): string =>
  text
    // A picture or a link is said by its words; the address is never read.
    .replace(/!?\[([^\]\n]*)\]\([^)\n]*\)/g, '$1')
    // A bare address: said as "a link", its own trailing punctuation kept for the sentence.
    .replace(/https?:\/\/[^\s<>)]+/g, (url) => LINK_SAID + (/[.,;:!?]+$/.exec(url)?.[0] ?? ''))
    .replace(/`([^`\n]*)`/g, '$1')
    .replace(/~~([^~\n]+)~~/g, '$1')
    .replace(/\*\*([^*\n]+)\*\*/g, '$1')
    .replace(/__([^_\n]+)__/g, '$1')
    // ⛔ Only marks that HUG their words: "5 * 2" and "LOI_-_13368_Main.pdf" are words, not emphasis.
    .replace(/(?<![*\w])\*(?=\S)([^*\n]+?)(?<=\S)\*(?![*\w])/g, '$1')
    .replace(/(?<![_\w])_(?=\S)([^_\n]+?)(?<=\S)_(?![_\w])/g, '$1')
    // Pictures of words (a check mark, a warning sign) are not words: a voice reads their NAMES.
    .replace(/[\p{Extended_Pictographic}\u{FE0F}\u{200D}]/gu, '')
    .replace(/\s+/g, ' ')
    .trim();

/** The word (letters and inner dots) that ends at `end` - what stands before a full stop. */
const wordBefore = (text: string, end: number): string => {
  let start = end;
  while (start > 0 && /[A-Za-z.]/.test(text[start - 1])) start -= 1;
  return text.slice(start, end).replace(/^\.+/, '');
};

/**
 * One run of plain text as its sentences. A sentence ends at `.`, `?` or `!` FOLLOWED BY A SPACE (or the end) - so a
 * decimal point, a thousands figure and a file name never end one - and not after an abbreviation.
 */
const sentencesOf = (text: string): string[] => {
  const out: string[] = [];
  let from = 0;
  for (let i = 0; i < text.length; i++) {
    const ch = text[i];
    if (ch !== '.' && ch !== '?' && ch !== '!') continue;
    // Closing quotes and brackets belong to the sentence they close.
    let end = i + 1;
    while (end < text.length && /["')\]]/.test(text[end])) end += 1;
    if (end < text.length && text[end] !== ' ') continue;
    if (ch === '.' && ABBREVIATIONS.has(wordBefore(text, i))) continue;
    const sentence = text.slice(from, end).trim();
    if (sentence) out.push(sentence);
    from = end;
    i = end - 1;
  }
  const tail = text.slice(from).trim();
  if (tail) out.push(/[.?!:]["')\]]*$/.test(tail) ? tail : `${tail}.`);
  return out;
};

/** A heading, a list item or a paragraph: cleaned, then split. Nothing at all when only marks were there. */
const said = (text: string): string[] => {
  const words = plain(text);
  return /[\p{L}\p{N}]/u.test(words) ? sentencesOf(words) : [];
};

/** An answer's markdown as the sentences a voice says, in order. */
export const toSpeech = (markdown: string): string[] => {
  const lines = markdown.replace(/\r\n?/g, '\n').split('\n');
  const out: string[] = [];
  let paragraph: string[] = [];
  const flush = (): void => {
    if (paragraph.length) out.push(...said(paragraph.join(' ')));
    paragraph = [];
  };

  for (let i = 0; i < lines.length; i++) {
    const line = lines[i];

    if (FENCE.test(line)) {
      flush();
      // To the closing fence, or to the end when it never closes: either way it is code, never read.
      let j = i + 1;
      while (j < lines.length && !FENCE.test(lines[j])) j += 1;
      out.push(CODE_SAID);
      i = j;
      continue;
    }

    if (TABLE_LINE.test(line) && i + 1 < lines.length && TABLE_LINE.test(lines[i + 1])) {
      flush();
      let j = i;
      let rows = 0;
      while (j < lines.length && TABLE_LINE.test(lines[j])) {
        if (!TABLE_SEPARATOR.test(lines[j])) rows += 1;
        j += 1;
      }
      // The first line is the header: it names the columns, it is not a row of them.
      out.push(TABLE_SAID(Math.max(0, rows - 1)));
      i = j - 1;
      continue;
    }

    if (!line.trim() || RULE_LINE.test(line)) { flush(); continue; }

    const heading = /^\s{0,3}#{1,6}\s+(.*?)\s*#*\s*$/.exec(line);
    if (heading) { flush(); out.push(...said(heading[1])); continue; }

    // ⛔ "1." is a marker only with a space after it: "1.25x is the floor" begins with a figure.
    const item = /^\s*(?:[-*+]|\d{1,3}[.)])\s+(.*)$/.exec(line);
    if (item) { flush(); out.push(...said(item[1])); continue; }

    paragraph.push(line.replace(/^\s*>+\s?/, ''));
  }
  flush();
  return out;
};

/**
 * An answer with its corrections. ⭐ An amber note under an answer says a thing the answer claimed did NOT happen
 * ("Nothing was opened - ..."). It is not part of the answer's text, and an answer heard without it is worse than
 * silence - so each note is said right after, as a note.
 */
export const speechForAnswer = (answer: { content: string; notes?: string[] }): string[] => [
  ...toSpeech(answer.content),
  ...(answer.notes ?? []).flatMap((note) => {
    const words = toSpeech(note);
    return words.length ? [`Note: ${words.join(' ')}`] : [];
  }),
];

/**
 * An answer STREAMS in: what has FINISHED landing, and what is still arriving.
 *
 * ⛔ A block is done only when a blank line follows it. Reading a table that is still arriving would read its first
 *   rows as prose - the cell-by-cell reading this file forbids - so everything after the last blank line is held back.
 * ⛔ A blank line INSIDE an open code fence ends nothing.
 */
export const completeBlocks = (text: string): { done: string; rest: string } => {
  const normal = text.replace(/\r\n?/g, '\n');
  let inFence = false;
  let cut = -1;
  let cutEnd = -1;
  let at = 0;
  for (const line of normal.split('\n')) {
    if (FENCE.test(line)) inFence = !inFence;
    else if (!inFence && !line.trim() && at > 0 && at <= normal.length) { cut = at - 1; cutEnd = at + line.length; }
    at += line.length + 1;
  }
  if (cut < 0) return { done: '', rest: normal };
  // The cut is the newline before a blank line; a run of blank lines is one boundary.
  const done = normal.slice(0, cut).replace(/\s+$/, '');
  const rest = normal.slice(Math.min(cutEnd + 1, normal.length)).replace(/^\n+/, '');
  return done ? { done, rest } : { done: '', rest: normal };
};
